import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { LoginResult } from "@hik/contracts";
import { ApiError, post } from "../../lib/api";
import { Button } from "../../ui/Button";
import { Checkbox, ErrorSummary, TextField, useFieldError } from "../../ui/Field";
import { AuthLayout } from "./AuthLayout";

export function SignupPage() {
  const [form, setForm] = useState({ companyName: "", fullName: "", email: "", password: "", createEmployeeRecord: true, acceptTerms: false });
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fe = useFieldError();
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ session: LoginResult["session"] }>("/auth/signup", form);
      qc.setQueryData(["session"], r.session);
      navigate("/ayarlar/baslangic", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Şirket hesabı açın" lead="Birkaç dakikada kurulur. Çalışanlarınızı sonra tek tek ya da Excel dosyasıyla eklersiniz." footer={<p>Hesabınız var mı? <Link to="/giris">Giriş yapın</Link></p>}>
      <form onSubmit={submit} noValidate className="form">
        <ErrorSummary message={error?.message} fields={error?.fields} />
        <TextField label="Şirket adı" value={form.companyName} onChange={set("companyName")} autoComplete="organization" required error={fe(error?.fields.companyName)} />
        <TextField label="Adınız soyadınız" value={form.fullName} onChange={set("fullName")} autoComplete="name" required error={fe(error?.fields.fullName)} />
        <TextField label="İş e-postanız" type="email" value={form.email} onChange={set("email")} autoComplete="email" required error={fe(error?.fields.email)} />
        <TextField
          label="Parola"
          type="password"
          value={form.password}
          onChange={set("password")}
          autoComplete="new-password"
          hint="En az 10 karakter. Uzun bir cümle hatırlaması kolay ve güvenlidir."
          required
          error={fe(error?.fields.password)}
        />
        <Checkbox label="Ben de bu şirketin çalışanıyım (izin ve bordro işlemlerimi buradan yapacağım)" checked={form.createEmployeeRecord} onChange={set("createEmployeeRecord")} />
        <Checkbox label="Hizmet koşullarını ve kişisel verilerin işlenmesine ilişkin aydınlatma metnini okudum." checked={form.acceptTerms} onChange={set("acceptTerms")} />
        <Button type="submit" variant="primary" size="lg" busy={busy} disabled={!form.acceptTerms} className="btn--block">
          Şirket hesabını oluştur
        </Button>
      </form>
    </AuthLayout>
  );
}
